"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import type { Platform } from "@/payload-types";
import { getImageData } from "@/lib/media-utils";
import { cn } from "@/lib/utils";

// Real anchor (crawlable href) that still switches platforms in-place on click
const MotionLink = motion.create(Link);

interface PlatformArcCarouselProps {
  platforms: Platform[];
  activeIndex: number;
  onSelect: (index: number) => void;
}

// ── Orbit geometry ──
const ANGLE_PER_ITEM = 28; // degrees between each platform on the orbit
const ORBIT_RADIUS_DESKTOP = 320; // px — fits within the center column
const ORBIT_RADIUS_MOBILE = 240; // tighter orbit so the side ships stay closer in and don't sit as low

// Arc SVG: separate larger radius for the planet-horizon decorative line
const ARC_VIEWBOX_W = 800;
const ARC_VIEWBOX_H = 400;
const ARC_CIRCLE_CX = 400;
const ARC_CIRCLE_CY = 620; // center well below viewport → only top curve visible
const ARC_CIRCLE_R = 420; // large enough for a gentle curvature

const toRad = (deg: number) => (deg * Math.PI) / 180;

export function PlatformArcCarousel({
  platforms,
  activeIndex,
  onSelect,
}: PlatformArcCarouselProps) {
  const n = platforms.length;

  // Show full orbit on mobile (single-column, full-width) and on 2xl+ desktops.
  // Between lg and 2xl the carousel column is too narrow — show only the active ship.
  const [showOrbit, setShowOrbit] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      setShowOrbit(w < 1024 || w >= 1536);
      setIsMobile(w < 1024);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const orbitRadius = isMobile ? ORBIT_RADIUS_MOBILE : ORBIT_RADIUS_DESKTOP;
  // Scale factors — 10% smaller on mobile so the icons feel less crowded
  const activeScale = isMobile ? 1.35 : 1.5;
  const sideScale = isMobile ? 0.9 : 1;
  const farScale = isMobile ? 0.36 : 0.4;

  return (
    <div className="flex h-full flex-col justify-center lg:justify-end">
      {/* ── Globe-orbit carousel selector ── */}
      <div className="relative h-56 lg:h-72">
        {/* SVG planet-horizon arc line */}
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full overflow-visible translate-y-[40px]"
          viewBox={`0 0 ${ARC_VIEWBOX_W} ${ARC_VIEWBOX_H}`}
          preserveAspectRatio="xMidYMax meet"
        >
          <defs>
            <linearGradient id="arcGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="white" stopOpacity="0" />
              <stop offset="15%" stopColor="white" stopOpacity="0" />
              <stop offset="35%" stopColor="white" stopOpacity="0.5" />
              <stop offset="50%" stopColor="white" stopOpacity="0.5" />
              <stop offset="65%" stopColor="white" stopOpacity="0.5" />
              <stop offset="85%" stopColor="white" stopOpacity="0" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
            <clipPath id="arcClip">
              <rect x="0" y="0" width={ARC_VIEWBOX_W} height={ARC_VIEWBOX_H - 60} />
            </clipPath>
          </defs>
          <circle
            cx={ARC_CIRCLE_CX}
            cy={ARC_CIRCLE_CY}
            r={ARC_CIRCLE_R}
            fill="none"
            stroke="url(#arcGradient)"
            strokeWidth="3"
            clipPath="url(#arcClip)"
          />
        </svg>

        {/* Revolving orbit: each platform slides along the arc */}
        {platforms.map((platform, i) => {
          // Circular offset from selected (-n/2 .. +n/2)
          let offset = ((i - activeIndex) % n + n) % n;
          if (offset > n / 2) offset -= n;

          // Render -2 to +2: outer positions are invisible entry/exit points
          if (Math.abs(offset) > 2) return null;

          const { url, alt } = getImageData(
            platform.silhouette,
            platform.name,
          );
          if (!url) return null;

          const angleDeg = offset * ANGLE_PER_ITEM;
          const angleRad = toRad(angleDeg);
          const absOffset = Math.abs(offset);
          // Push the side ships an extra 10px outward (mirrored on both sides)
          const sideKick = absOffset === 1 ? Math.sign(offset) * 10 : 0;
          // Lift the active center ship above the orbit. Mobile gets more lift
          // so the active vessel reads clearly above the side ships in the
          // tighter mobile layout.
          const activeLift =
            absOffset === 0 ? (isMobile ? -70 : -10) : 0;
          const x = Math.sin(angleRad) * orbitRadius + sideKick;
          const y = (1 - Math.cos(angleRad)) * orbitRadius + activeLift;

          const scale =
            absOffset === 0 ? activeScale : absOffset === 1 ? sideScale : farScale;
          const opacity =
            absOffset === 0 ? 1 : absOffset === 1 && showOrbit ? 0.4 : 0;
          const isActive = offset === 0;

          return (
            <div
              key={platform.id}
              className="absolute left-1/2 top-[40px] lg:top-[76px]"
              style={{ marginLeft: "-60px" }}
            >
              <MotionLink
                href={`/platforms/${platform.slug}`}
                prefetch={false}
                draggable={false}
                aria-label={platform.name}
                onClick={(e) => {
                  e.preventDefault();
                  onSelect(i);
                }}
                onKeyDown={(e) => {
                  // Anchors don't activate on Space like buttons do
                  if (e.key === " ") {
                    e.preventDefault();
                    onSelect(i);
                  }
                }}
                aria-current={isActive ? "page" : undefined}
                tabIndex={opacity === 0 ? -1 : undefined}
                animate={{ x, y, scale, opacity }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
                className={cn(
                  "relative block h-14 w-[120px]",
                  isActive ? "z-20" : "z-10 cursor-pointer"
                )}
              >
                <Image
                  src={url}
                  alt={alt}
                  fill
                  className="object-contain object-center"
                  sizes="120px"
                />
              </MotionLink>
            </div>
          );
        })}
      </div>
    </div>
  );
}
